/**
 * Renders the "Used by: <experiment name> (<status>), ..." backlinks
 * displayed under the status chip on EXPERIMENT-status PageVersion /
 * ContentVersion rows (issue #255 AC1).
 *
 * Each name is a TanStack Router `<Link>` to `/experiments/$experimentId`
 * so the operator can jump from the page/content detail surface straight
 * to the experiment that consumes the version. When the input array is
 * empty we render a muted "Not referenced" line — the operator's signal
 * that the EXPERIMENT version is orphaned and safe to archive.
 *
 * The testID prefix differs between the page-detail and content-detail
 * call sites so generic E2E selectors can target each surface
 * independently. Each backlink renders with
 * `data-testid={`${testIdPrefix}-${experimentId}`}` per the issue's AC.
 */

import type { ReactElement } from "react";
import { Link } from "@tanstack/react-router";
import { Box, Typography } from "@mui/material";
import type { ReferencingExperimentRef } from "@ottopilot/shared";

export function ExperimentBacklinks({
  refs,
  testIdPrefix,
}: {
  refs: ReadonlyArray<ReferencingExperimentRef>;
  /**
   * Per-link testID prefix. The full per-link testID is
   * `${testIdPrefix}-${experimentId}`. Two call sites today:
   *   - `page-detail-experiment-row-backlink`
   *   - `content-item-detail-experiment-row-backlink`
   */
  testIdPrefix: string;
}): ReactElement {
  if (refs.length === 0) {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontStyle: "italic" }}
        data-testid={`${testIdPrefix}-empty`}
      >
        Not referenced
      </Typography>
    );
  }

  return (
    <Box
      sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 0.5 }}
      data-testid={`${testIdPrefix}-list`}
    >
      <Typography variant="caption" color="text.secondary">
        Used by:
      </Typography>
      {refs.map((ref, idx) => (
        <Typography
          key={ref.id}
          variant="caption"
          color="text.secondary"
          component="span"
          sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.25 }}
        >
          <Link
            to="/experiments/$experimentId"
            params={{ experimentId: ref.id }}
            data-testid={`${testIdPrefix}-${ref.id}`}
            // Use the secondary color so the operator sees this is a
            // link without overpowering the page's denser content above.
            style={{ color: "inherit", textDecoration: "underline" }}
          >
            {ref.name}
          </Link>
          <Typography variant="caption" color="text.secondary" component="span">
            ({ref.status})
          </Typography>
          {idx < refs.length - 1 ? (
            <Typography variant="caption" color="text.secondary" component="span">
              ,
            </Typography>
          ) : null}
        </Typography>
      ))}
    </Box>
  );
}
